// useEditorSetup.js
// Hook to manage the complete editor setup including collaboration and editor view

import {useEffect, useLayoutEffect, useRef, useState, startTransition} from "react";
import {createEditorView} from "../core";
import {setupLoadingStateManager} from "../helpers/loadingStateManager";
import {logYjsUpdate} from "../helpers/utils";
import {useClassificationMenu} from "./useClassificationMenu";
import {useEditorLoading} from "./useEditorLoading";
import {useYjsConnection} from "./useYjsConnection";
import useYDocStore from "../../../stores/useYDocStore";
import {getOrCreateSession} from "../../../session/awpSessionContext";
import {hydrateDocumentMetaFromApi} from "../../../utils/hydrateDocumentMeta";
import {seedYjsDocFromPmJsonIfEmpty} from "../helpers/seedYjsDocFromPmJsonIfEmpty";

/**
 * Hook to set up ProseMirror editor with collaboration
 *
 * @param {Object} params
 * @param {string} params.documentId - Document identifier for collaboration
 * @param {Object} params.user - User object with uid, email, displayName
 * @param {string} params.websocketUrl - WebSocket server URL for collaboration
 * @param {React.RefObject<HTMLElement>} params.editorMountRef - DOM mount point ref
 * @param {number} params.editorMountTick - Bumps when the mount ref attaches (layout retry only)
 * @param {Function} params.onReady - Callback when editor is ready (receives EditorView)
 * @param {Function} params.onChange - Callback when document changes (receives EditorView)
 * @param {Object} params.snippetDrop - Snippet drop plugin options (optional)
 * @param {Function} params.onInlineInteractionChange - Callback when inline interaction target changes (payload or null)
 * @param {boolean} params.inlineInteractionPopovers - Render inline info popovers via ProseMirror plugin views
 * @param {Function} params.onSelectionChange - Callback when selection state changes (optional)
 * @returns {Object} { viewRef, viewTick, isLoading, loadingStage, providerRef, destroyedViewsRef, classificationMenu, collabMaps, headerMetaTick }
 */
export function useEditorSetup({
                                   documentId,
                                   user,
                                   websocketUrl,
                                   editorMountRef,
                                   editorMountTick = 0,
                                   onReady,
                                   onChange,
                                   snippetDrop,
                                   onInlineInteractionChange,
                                    inlineInteractionPopovers = false,
                                   onSelectionChange,
                                   editorMode = 'full',
                                   readOnly = false,
                                   seedPmJson = null,
                               }) {
    const viewRef = useRef(null);
    const tryInitEditorRef = useRef(() => {});
    const editorInitRequestedRef = useRef(false);
    const ydocRef = useRef(null);
    const yDocMetaRef = useRef(null);
    const yReferenceMarksRef = useRef(null);
    const yCommentsRef = useRef(null);
    const ySourcePackageRef = useRef(null);
    const providerRef = useRef(null);
    const destroyedViewsRef = useRef(new WeakMap());
    const readOnlyRef = useRef(readOnly);

    // Use refs for config objects to avoid re-initialization when they change
    // These are updated before the effect runs, so the editor gets the latest values
    const userRef = useRef(user);
    const snippetDropRef = useRef(snippetDrop);
    const onReadyRef = useRef(onReady);
    const onChangeRef = useRef(onChange);
    const onInlineInteractionChangeRef = useRef(onInlineInteractionChange);
    const onSelectionChangeRef = useRef(onSelectionChange);
    const seedPmJsonRef = useRef(seedPmJson);
    const classificationMenuRef = useRef(null);

    // Update refs on every render so callbacks always have latest values
    userRef.current = user;
    snippetDropRef.current = snippetDrop;
    onReadyRef.current = onReady;
    onChangeRef.current = onChange;
    onInlineInteractionChangeRef.current = onInlineInteractionChange;
    onSelectionChangeRef.current = onSelectionChange;
    seedPmJsonRef.current = seedPmJson;
    readOnlyRef.current = readOnly;

    const [viewTick, setViewTick] = useState(0);
    const [collabMaps, setCollabMaps] = useState(null);
    const [headerMetaTick, setHeaderMetaTick] = useState(0);

    const {
        isLoading,
        loadingStage,
        setIsLoading,
        setLoadingStage,
        startLoading,
    } = useEditorLoading(!!documentId);

    const classificationMenu = useClassificationMenu();
    classificationMenuRef.current = classificationMenu;
    const connection = useYjsConnection({
        documentId,
        user: userRef.current,
        websocketUrl,
    });

    useEffect(() => {
        if (!documentId || !connection) return;

        const {
            ydoc,
            yDocMeta,
            yReferenceMarks,
            yComments,
            ySourcePackage,
            provider,
        } = connection;

        let destroyed = false;
        const destroyedViews = destroyedViewsRef.current;

        // Add global error handler for y-websocket protocol errors
        const handleError = (event) => {
            if (event.error && event.error.message && event.error.message.includes('Unknown message type')) {
                console.error('[y-websocket] Caught protocol error globally - preventing crash');
                event.preventDefault();
            }
        };
        window.addEventListener('error', handleError);

        ydocRef.current = ydoc;
        yDocMetaRef.current = yDocMeta;
        yReferenceMarksRef.current = yReferenceMarks;
        yCommentsRef.current = yComments;
        ySourcePackageRef.current = ySourcePackage;
        providerRef.current = provider;
        startTransition(() => setCollabMaps({yDocMeta, ydoc}));
        startLoading('connecting');

        const syncClientSessionToDocumentMeta = () => {
            const {sessionId, sessionStart} = getOrCreateSession(userRef.current?.uid);
            // Write only after Yjs sync so we do not push a local-only doc state ahead of server content.
            yDocMeta.set("clientSessionId", sessionId);
            yDocMeta.set("clientSessionStart", sessionStart);
        };

        const tryInitEditor = async () => {
            const mount = editorMountRef?.current;
            if (destroyed || viewRef.current || !mount) {
                return;
            }
            if (!provider.synced) {
                return;
            }
            syncClientSessionToDocumentMeta();
            try {
                const hydrated = await hydrateDocumentMetaFromApi(documentId, ydoc, yDocMeta);
                if (hydrated && !destroyed) {
                    startTransition(() => setHeaderMetaTick((t) => t + 1));
                }
            } catch (err) {
                console.warn('[editor] documentMeta API hydration failed', err);
            }
            if (destroyed || viewRef.current) {
                return;
            }
            if (seedPmJsonRef.current) {
                seedYjsDocFromPmJsonIfEmpty(ydoc, seedPmJsonRef.current);
            }
            initEditor(mount);
        };

        const onDocumentReadyForEditor = () => {
            editorInitRequestedRef.current = true;
            tryInitEditor();
        };

        // Setup loading state manager
        const {hideLoadingWhenViewReady, cleanup: cleanupLoading} = setupLoadingStateManager({
            ydoc,
            provider,
            setIsLoading,
            setLoadingStage,
            onSynced: onDocumentReadyForEditor,
            getView: () => viewRef.current,
        });

        // Setup Yjs update logging in development
        const onYDocUpdate = (update, origin) => {
            logYjsUpdate(ydoc, origin);
        };

        if (process.env.NODE_ENV === 'development') {
            ydoc.on("update", onYDocUpdate);
        }

        // Initialize editor
        const initEditor = (mountEl) => {
            const mount = mountEl || editorMountRef?.current;
            if (destroyed || viewRef.current || !mount) return;

            if (!ydoc) {
                console.warn("[editor] ydoc is null, cannot initialize editor");
                return;
            }

            try {
                const view = createEditorView({
                    mount,
                    documentId,
                    ydoc,
                    awareness: provider.awareness,
                    user: userRef.current,
                    provider,
                    classificationMenu: classificationMenuRef.current,
                    snippetDrop: snippetDropRef.current,
                    onInlineInteractionChange: onInlineInteractionChangeRef.current,
                    inlineInteractionPopovers,
                    onSelectionChange: onSelectionChangeRef.current,
                    yDocMeta,
                    yReferenceMarks,
                    yComments,
                    editorMode,
                    getReadOnly: () => readOnlyRef.current,
                });

                // Populate the Y.Doc store so any component or plugin can access the
                // shared types without prop drilling.
                useYDocStore.getState().initialize(ydoc, provider);

                viewRef.current = view;
                startTransition(() => setViewTick((t) => t + 1));

                hideLoadingWhenViewReady();

                // Wrap dispatch to prevent calls after destruction
                const originalDispatch = view.dispatch.bind(view);
                view.dispatch = function (...args) {
                    if (!destroyed && !destroyedViews.has(view)) {
                        originalDispatch(...args);
                    }
                };

                // Setup onChange listener
                if (onChangeRef.current) {
                    const originalUpdateState = view.updateState.bind(view);
                    view.updateState = function (state) {
                        if (!destroyed && !destroyedViews.has(view)) {
                            originalUpdateState(state);
                            if (onChangeRef.current) {
                                onChangeRef.current(view);
                            }
                        }
                    };
                }

                if (onReadyRef.current) {
                    onReadyRef.current(view, provider);
                }
            } catch (err) {
                console.error("[editor] failed to create editor view", err);
                setIsLoading(false);
            }
        };

        tryInitEditorRef.current = tryInitEditor;

        // Cleanup
        return () => {
            destroyed = true;
            editorInitRequestedRef.current = false;
            tryInitEditorRef.current = () => {};
            window.removeEventListener('error', handleError);
            cleanupLoading();

            useYDocStore.getState().cleanup();

            if (process.env.NODE_ENV === 'development') {
                ydoc.off("update", onYDocUpdate);
            }

            if (viewRef.current) {
                destroyedViews.set(viewRef.current, true);
                viewRef.current.destroy();
            }
            viewRef.current = null;

            providerRef.current = null;
            ydocRef.current = null;
            yDocMetaRef.current = null;
            yReferenceMarksRef.current = null;
            yCommentsRef.current = null;
            ySourcePackageRef.current = null;
            startTransition(() => setCollabMaps(null));

            startTransition(() => setViewTick((t) => t + 1));
        };
        // Only re-initialize when these core values change.
        // user, onReady, onChange, snippetDrop, and interaction callbacks are accessed via refs
        // so they can be updated without destroying the editor.
        // editorMountRef is a stable React ref object; its .current changes don't need to be
        // a dependency — the layoutEffect below handles retrying when the mount becomes available.
        // setIsLoading, setLoadingStage, startLoading are stable useState setters — excluded deliberately.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentId, connection, inlineInteractionPopovers]);

    // Retry init only after loading manager requested it but mount was not ready yet
    useLayoutEffect(() => {
        if (!connection || !editorMountRef?.current || viewRef.current) return;
        if (!editorInitRequestedRef.current) return;
        tryInitEditorRef.current();
    }, [connection, editorMountTick, editorMountRef]);

    // Re-evaluate editable() when readOnly toggles (Submit / Modify).
    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;
        view.setProps({editable: () => !readOnlyRef.current});
        setViewTick((t) => t + 1);
    }, [readOnly]);

    return {
        viewRef,
        viewTick,
        isLoading,
        loadingStage,
        providerRef,
        destroyedViewsRef,
        classificationMenu,
        collabMaps,
        headerMetaTick,
    };
}

